import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from '@shared/routing';
import MediaQuery from 'react-responsive';

import {
	Customer,
	TravellerFieldEnum,
	InputTraveller,
	useOrderQuery,
	useSaveOrderServicesMutation,
	useFfpCompleteOrderMutation,
	OrderStatus
} from '@websky/graphql';
import { getUserValue, MOBILE_MIN_WIDTH } from '../../../../../../../utils';
import { ServiceOpenEvent, StepType } from '../../../../../../types';
import Toolbar from '../../../../../../../Toolbar';
import { useTheme } from '../../../../../../../theme';
import { FooterMobile } from '@shared/ui';
import {
	getBonusForFlight,
	getIsBooked,
	getIsBookedOrConfirmed,
	getIsCancelled,
	getIsConfirmedOrCancelled,
	getIsContactsInfoIsEmpty,
	getIsFareLockSelected,
	getIsNew,
	getIsTravellersDataCompletionRequired,
	getIsTravellersDataFilled,
	getOrder as getRootOrder,
	getOrderId,
	getPaymentIsRequired,
	getPriceInMiles,
	getSelectedInsuranceCodes,
	getServicesFallbackURL,
	getTotalInsurancePrice
} from '../../../../../../store/order/selectors';
import { Money as MoneyComponent } from '../../../../../../../Money';
import { useUpsaleWarning } from '../../../../Upsale/hooks';
import Warning from '../../../../Upsale/Warning/Warning';
import { getTotalPriceToPay } from '../../../../../../store/orderPrice/selectors';
import PassengersToolbarCheckbox from '../PassengersToolbarCheckbox/PassengersToolbarCheckbox';
import { useCustomerContactsForm } from '../../../../../../../CustomerContactsForm/hooks';
import { Contacts } from '../../../../../../../CustomerContactsForm/CustomerContactsForm';
import { fillOrder } from '../../../../../../store/order/actions';
import CustomerContactsModal from '../../../../../../../CustomerContactsForm/Modal/CustomerContactsModal';
import { getPassengerState } from '../../../../../../store/passengers/selectors';
import { NULL_COMPONENT, useRenderers } from '../../../../../../../renderProps';
import { InsuranceUpsaleModal } from '../../../../../../../InsurancePage';
import { useInsuranceUpsaleModal } from '../../../../../../../InsurancePage/components/InsuranceUpsaleModal/InsuranceUpsaleModal.hook';
import { saveOrderServicesParamsFactory } from '../../../../../../../InsurancePage/store/utils';
import AIPurchasePredictionService from '../../../../../../../AIPurchasePredictionService/components/AIPurchasePredictionService';
import { ServicePayload, setMultipleSelectedServices } from '../../../../../../store/selectedServices/actions';
import { SelectedService } from '../../../../../../../AIPurchasePredictionService/types';
import { useAiPurchasePredictionService } from '../../../../../../../AIPurchasePredictionService/hook';
import { isServicesSaving } from '../../../../../../store/servicesIsSaving/selectors';
import { WEBSKY_SERVICE_OPEN_EVENT } from '../../../../../../constants';
import WebskyAnalyticsApollo from '../../../../../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { PurchaseShowPopupAction, WxEvents } from '../../../../../../../DataLayerAnalytics/types';
import { getAiPurchasePopupGoal } from '../../../../../../../DataLayerAnalytics/utils';
import SimpleLoader from '../../../../../../../SimpleLoader';
import InsuranceModalConfirmINN from '../../../../../../../InsuranceModalConfirmINN';
import { usePassengersToConfirmIIN } from '../../../../../../../InsuranceModalConfirmINN/hooks';
import { isGdsServiceType } from '../../../../../../../AIPurchasePredictionService/utils';
import { PassengersToolbarProps } from '../../types';

const PassengersToolbarWithoutForm: React.FC<PassengersToolbarProps> = ({
	formRef,
	currentStep,
	setNextStep,
	disableBooking,
	loading,
	formState,
	onConfirmConsents,
	showConsents = true,
	updateTravellers
}) => {
	const { t } = useTranslation('Checkout');
	const { CheckoutStyles: checkoutTheme, PassengersToolbar: toolbarTheme } = useTheme('Checkout');

	const dispatch = useDispatch();
	const history = useHistory();

	const isNew = useSelector(getIsNew);
	const isBookedOrConfirmed = useSelector(getIsBookedOrConfirmed);
	const isConfirmedOrCancelled = useSelector(getIsConfirmedOrCancelled);
	const isBooked = useSelector(getIsBooked);
	const totalInsurancePrice = useSelector(getTotalInsurancePrice);
	const totalPriceToPay = useSelector(getTotalPriceToPay);
	const servicesRedirectURL = useSelector(getServicesFallbackURL);
	const { toggleWarningIfOrderConfirmed, showWarning, closeWarning } = useUpsaleWarning();

	const isOrderCancelled = useSelector(getIsCancelled);
	const bonusForFlight = useSelector(getBonusForFlight);
	const priceInMilesForFlight = useSelector(getPriceInMiles);
	const isPaymentRequired = useSelector(getPaymentIsRequired);
	const isTravellersDataFilled = useSelector(getIsTravellersDataFilled);
	const isTravellersDataCompletionRequired = useSelector(getIsTravellersDataCompletionRequired);
	const isFareLockSelected = useSelector(getIsFareLockSelected);
	const isContactsInfoEmpty = useSelector(getIsContactsInfoIsEmpty);
	const passengersValues = useSelector(getPassengerState);
	const order = useSelector(getRootOrder);
	const orderId = useSelector(getOrderId);
	const selectedInsuranceCodes = useSelector(getSelectedInsuranceCodes);
	const isOrderServicesSaving = useSelector(isServicesSaving);

	let handleSubmit: (passengersValues: InputTraveller[]) => Promise<void>;

	if (!!updateTravellers?.valid) {
		handleSubmit = updateTravellers.handleSubmit;
	}

	const [servicesLoading, setServicesLoading] = useState(false);
	const { refetch: getOrder } = useOrderQuery({ skip: true });

	const [saveOrderServices, saveOrderServicesResult] = useSaveOrderServicesMutation();
	const [ffpCompleteOrder, ffpCompleteOrderResult] = useFfpCompleteOrderMutation();

	const {
		saveClientInfo,
		closeContactsModal,
		openContactsModal,
		isContactsModalOpen,
		loading: isClientInfoSaving
	} = useCustomerContactsForm();

	const insuranceUpsaleModal = useInsuranceUpsaleModal({ strategy: 'checkout-before-payment' });
	const aiPurchasePredictionService = useAiPurchasePredictionService();

	const {
		renderPassengersToolbarAdditional: RenderPassengersToolbarAdditionalOverride = NULL_COMPONENT
	} = useRenderers();

	const {
		passengersToConfirmIIN,
		isNeededToConfirmIIN,
		closeInsuranceModal,
		openInsuranceModal,
		isInsuranceModalOpen,
		isSaveOrderServicesLoading,
		saveInsuranceAndUpdateOrder
	} = usePassengersToConfirmIIN(setNextStep);

	const goToPayment = useCallback(async () => {
		if (handleSubmit) {
			const values = passengersValues.map<InputTraveller>(traveller => ({
				id: traveller.id,
				values: traveller.values?.map(value => ({
					type: value.type,
					name: value.name,
					value: value.value
				}))
			}));

			await handleSubmit(values);

			if (!isTravellersDataFilled) {
				return;
			}
		} else if (formRef?.current) {
			formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));

			return;
		}

		if (showWarning) {
			return toggleWarningIfOrderConfirmed();
		}

		if (isPaymentRequired && (isContactsInfoEmpty || isNeededToConfirmIIN)) {
			if (isContactsInfoEmpty) {
				openContactsModal();
			} else if (isNeededToConfirmIIN) {
				openInsuranceModal();
			}

			return;
		}

		if (order.ffpInfo.skipPayment) {
			const result = await ffpCompleteOrder({ variables: { id: orderId } });
			const ffpCompleteOrderResult = result?.data?.FfpCompleteOrder;

			if (ffpCompleteOrderResult?.result && ffpCompleteOrderResult?.order) {
				dispatch(fillOrder(ffpCompleteOrderResult.order));
			}
		} else {
			setNextStep();
		}
	}, [
		dispatch,
		handleSubmit,
		passengersValues,
		showWarning,
		isPaymentRequired,
		isContactsInfoEmpty,
		isTravellersDataFilled,
		setNextStep,
		order.ffpInfo.skipPayment
	]);

	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const onContinuePurchasePredictionService = useCallback(async () => {
		if (onConfirmConsents) {
			onConfirmConsents();

			return;
		}
		if (isMountedRef.current) {
			setServicesLoading(true);
		}

		try {
			await aiPurchasePredictionService.onContinue(currentStep.type, goToPayment);
		} finally {
			if (isMountedRef.current) {
				setServicesLoading(false);
			}
		}
	}, [onConfirmConsents, currentStep.type, goToPayment, aiPurchasePredictionService.onContinue]);

	const saveClientInfoAndUpdateOrder = async (data: Contacts) => {
		await saveClientInfo({ variables: { ...data, orderId } });
		const { data: getOrderResult } = await getOrder({ id: orderId });

		if (getOrderResult?.Order) {
			dispatch(fillOrder(getOrderResult.Order));
		}

		closeContactsModal();

		if (isNeededToConfirmIIN) {
			openInsuranceModal();
		} else {
			setNextStep();
		}
	};

	const savePriorityInsurance = useCallback(
		async (selectedCodes: string[] = []) => {
			const selectedCodesSet = new Set<string>([...selectedInsuranceCodes, ...selectedCodes]);

			if (!selectedCodesSet.size) {
				return;
			}

			const travellerIds = order.travellers.map(traveller => traveller.id);
			const saveOrderParams = saveOrderServicesParamsFactory(travellerIds, [...selectedCodesSet]);

			const { data } = await saveOrderServices({
				variables: {
					params: {
						...saveOrderParams.params,
						id: order.id
					}
				}
			});

			if (data?.SaveOrderServices) {
				dispatch(fillOrder(data.SaveOrderServices));
			}

			await onContinuePurchasePredictionService();
		},
		[order, selectedInsuranceCodes, onContinuePurchasePredictionService, dispatch]
	);

	const saveAdditionalService = async (service: ServicePayload) => {
		dispatch(
			setMultipleSelectedServices(
				[
					{
						passengerId: service.passengerId,
						allowedSegments: service.allowedSegments,
						segmentId: service.segmentId,
						serviceId: service.serviceId,
						count: service.count,
						service: {
							id: service.service.id,
							type: service.service.type
						}
					}
				],
				{ custom_ecommerce_name: getAiPurchasePopupGoal(service.service.type, PurchaseShowPopupAction.Add) }
			)
		);
	};

	useEffect(() => {
		if (!isOrderServicesSaving && aiPurchasePredictionService.isAddedService) {
			goToPayment();
		}
	}, [isOrderServicesSaving, aiPurchasePredictionService.isAddedService]);

	useEffect(() => {
		if (aiPurchasePredictionService.isOpen) {
			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.AiPurchaseModalOpen, {
				servicesIds: [aiPurchasePredictionService.service.id]
			});
		}
	}, [aiPurchasePredictionService.isOpen]);

	const onChangeStep = useCallback(
		(service: SelectedService) => {
			const ecommerceName = isGdsServiceType(service.type)
				? getAiPurchasePopupGoal(service.type, PurchaseShowPopupAction.Change)
				: null;

			const event = new CustomEvent<ServiceOpenEvent>(WEBSKY_SERVICE_OPEN_EVENT, {
				detail: {
					service,
					callback: goToPayment,
					meta: { custom_ecommerce_name: ecommerceName }
				}
			});

			document.dispatchEvent(event);
		},
		[goToPayment]
	);

	const onAiPurchaseDisagree = () => {
		WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.AiPurchaseModalClose, {
			servicesIds: [aiPurchasePredictionService.service.id]
		});
		aiPurchasePredictionService.onDisagree(goToPayment);
	};

	const customerValues = React.useMemo(() => {
		const customer = order.customer as Customer;

		return {
			email: getUserValue(customer, TravellerFieldEnum.Email),
			phone: getUserValue(customer, TravellerFieldEnum.Phone)
		};
	}, [order.customer]);

	const goBack = useCallback(() => {
		history.goBack();
	}, [history]);

	const hasPriceOrMilesToPay =
		totalPriceToPay.amount > 0 || (order.ffpInfo.skipPayment && order.status !== OrderStatus.Confirmed);

	const showToolbar =
		(currentStep?.type !== StepType.Payment && !isOrderCancelled && hasPriceOrMilesToPay) ||
		!isTravellersDataFilled;

	const isDesktopToolbarLoading =
		updateTravellers?.loading || loading || saveOrderServicesResult.loading || servicesLoading;

	const hasLinkedTravellerError = React.useMemo(
		() => formState?.errors['passengers']?.some((passenger: any) => !!passenger?.linkedTraveller),
		[formState?.errors]
	);

	let isDisabledButton = disableBooking || hasLinkedTravellerError;

	if (isTravellersDataCompletionRequired) {
		isDisabledButton = isDisabledButton || (!handleSubmit && !isFareLockSelected);
	}

	const isFooterMobileDisabled =
		disableBooking || loading || saveOrderServicesResult.loading || servicesLoading || hasLinkedTravellerError;

	return (
		<>
			{(ffpCompleteOrderResult.loading || updateTravellers?.loading) && <SimpleLoader />}
			{showToolbar && (
				<>
					<Warning open={showWarning} redirectURL={servicesRedirectURL} onClose={closeWarning} />

					<InsuranceUpsaleModal
						insurance={insuranceUpsaleModal.priorityInsurance}
						isOpen={insuranceUpsaleModal.isUpsaleOpen}
						onAgree={() => insuranceUpsaleModal.onAgree(savePriorityInsurance)}
						onDisagree={() => insuranceUpsaleModal.onDisagree(onContinuePurchasePredictionService)}
					/>

					<AIPurchasePredictionService
						order={order}
						service={aiPurchasePredictionService.service}
						onAgree={service => aiPurchasePredictionService.onAgree(service, saveAdditionalService)}
						onChange={payload => aiPurchasePredictionService.onChange(payload, onChangeStep)}
						onDisagree={onAiPurchaseDisagree}
					/>

					<CustomerContactsModal
						initialValues={customerValues}
						onContactsSubmit={saveClientInfoAndUpdateOrder}
						isLoading={isClientInfoSaving}
						isOpen={isContactsModalOpen}
						onClose={closeContactsModal}
					/>

					<InsuranceModalConfirmINN
						isOpen={isInsuranceModalOpen}
						onClose={closeInsuranceModal}
						onSubmit={saveInsuranceAndUpdateOrder}
						isSubmitLoading={isSaveOrderServicesLoading}
						passengers={passengersToConfirmIIN}
					/>

					<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
						<Toolbar
							isLoading={isDesktopToolbarLoading}
							actionsRight={isBooked}
							hideNextButton={!hasPriceOrMilesToPay && isTravellersDataFilled}
							totalPrice={totalPriceToPay}
							priceInMiles={!isConfirmedOrCancelled && priceInMilesForFlight}
							onButtonClick={
								isBookedOrConfirmed
									? () => insuranceUpsaleModal.onContinue(onContinuePurchasePredictionService)
									: null
							}
							miles={!isConfirmedOrCancelled && bonusForFlight}
							buttonDisabled={isDisabledButton}
							additional={
								<>
									{isBooked && totalInsurancePrice && totalInsurancePrice.amount && (
										<div className={checkoutTheme.gdsTotalPrice}>
											{t('Insurance')}: <MoneyComponent money={totalInsurancePrice} />
										</div>
									)}

									<div>
										{showConsents && isNew && <PassengersToolbarCheckbox />}

										<RenderPassengersToolbarAdditionalOverride order={order} />
									</div>
								</>
							}
						/>
					</MediaQuery>

					<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
						{((showConsents && isNew) || RenderPassengersToolbarAdditionalOverride !== NULL_COMPONENT) && (
							<div className={toolbarTheme.toolbarCheckbox__wrapper}>
								{showConsents && isNew && <PassengersToolbarCheckbox />}

								<RenderPassengersToolbarAdditionalOverride order={order} />
							</div>
						)}

						<FooterMobile
							onBack={goBack}
							disabled={isFooterMobileDisabled}
							onContinue={
								isBookedOrConfirmed && hasPriceOrMilesToPay
									? () => insuranceUpsaleModal.onContinue(onContinuePurchasePredictionService)
									: isBookedOrConfirmed
									? undefined
									: null
							}
						/>
					</MediaQuery>
				</>
			)}
		</>
	);
};

export default PassengersToolbarWithoutForm;
